import * as React from 'react';
import {Box, Card, CardContent, Chip, Stack, Step, StepLabel, Stepper, Typography} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useTranslation} from 'react-i18next';
import type {WizardStep} from '../../hooks/useDashboard';

interface PayrollWizardCardProps {
    readonly periodLabel: string;
    readonly statusLabel: string;
    readonly isComplete: boolean;
    readonly steps: WizardStep[];
    readonly activeStepIndex: number;
    readonly onViewSteps: () => void;
}

export default function PayrollWizardCard({
                                               periodLabel,
                                               statusLabel,
                                               isComplete,
                                               steps,
                                               activeStepIndex,
                                               onViewSteps,
                                           }: Readonly<PayrollWizardCardProps>): React.ReactElement {
    const {t} = useTranslation();
    const activeStep = steps[activeStepIndex];

    return (
        <Card variant="outlined" sx={{borderRadius: 1}}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}
                       sx={{mb: 2}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 700, textAlign: 'left'}}>
                        {t('dashboard.wizard.title', {period: periodLabel})}
                    </Typography>
                    <Chip label={statusLabel} size="small" color={isComplete ? 'success' : 'warning'}
                          variant="outlined"/>
                </Stack>

                {/* Desktop/tablet: full stepper */}
                <Box sx={{display: {xs: 'none', md: 'block'}}}>
                    <Stepper activeStep={activeStepIndex} alternativeLabel>
                        {steps.map((step) => (
                            <Step key={step.key} completed={step.done}>
                                <StepLabel>{step.label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Mobile: compact step summary + link, matching the mobile reference layout */}
                <Box sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', justifyContent: 'space-between'}}>
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        {t('dashboard.wizard.stepSummary', {
                            current: activeStepIndex + 1,
                            total: steps.length,
                            stepName: activeStep?.label ?? '',
                        })}
                    </Typography>
                    <Chip
                        label={t('dashboard.wizard.viewSteps')}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        icon={<ArrowForwardIcon/>}
                        onClick={onViewSteps}
                        sx={{cursor: 'pointer'}}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
